import {
  IconButton,
  InputAdornment,
  TextField,
  type SxProps,
} from '@mui/material';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { useDebouncedValue } from '@/utils/useDebouncedValue';

import { Icon } from '../shared/icon';

export const SearchTextBox = forwardRef<
  HTMLInputElement,
  {
    sx?: SxProps;
    value: string;
    placeholder?: string;
    onChange: (value: string) => void;
    hideIcon?: boolean;
    onEnter?: () => void;
    onTab?: () => void;
    autoFocus?: boolean;
    debounceMs?: number;
  }
>(function (
  {
    sx,
    value,
    placeholder,
    onChange,
    hideIcon,
    onEnter,
    onTab,
    autoFocus,
    debounceMs,
  },
  ref
) {
  const [displayValue, setDisplayValue] = useState(value);
  const debouncedDisplay = useDebouncedValue(displayValue, debounceMs ?? 0);

  // Sync external value resets (e.g. parent clears the field programmatically)
  const prevValueRef = useRef(value);
  if (debounceMs && prevValueRef.current !== value) {
    prevValueRef.current = value;
    setDisplayValue(value);
  }

  useEffect(() => {
    if (debounceMs) {
      onChange(debouncedDisplay);
    }
    // onChange intentionally omitted — callers pass inline functions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDisplay, debounceMs]);

  const inputValue = debounceMs ? displayValue : value;

  const handleChange = (newValue: string) => {
    if (debounceMs) {
      setDisplayValue(newValue);
    } else {
      onChange(newValue);
    }
  };

  const handleClear = () => {
    if (debounceMs) {
      setDisplayValue('');
    }
    onChange('');
  };

  return (
    <TextField
      autoFocus={autoFocus}
      sx={{
        '.MuiInputBase-root': {
          height: '32px',
          paddingLeft: '4px',
          paddingRight: '4px',
        },
        '.MuiInputBase-input': {
          ...(!hideIcon && { paddingLeft: 0 }),
        },
        '.MuiInputAdornment-root': {
          margin: '0px',
        },
        ...sx,
      }}
      value={inputValue}
      onKeyDown={
        onEnter || onTab
          ? (e) => {
              if (onEnter && e.key === 'Enter') {
                onEnter();
              }

              if (onTab && e.key === 'Tab') {
                onTab();
              }
            }
          : undefined
      }
      inputRef={ref}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={placeholder}
      slotProps={{
        input: {
          startAdornment: !hideIcon && (
            <InputAdornment position='start'>
              <Icon
                iconClass='material-symbols-search'
                color='var(--mui-palette-text-disabled) !important'
              />
            </InputAdornment>
          ),
          endAdornment: inputValue && (
            <InputAdornment position='end'>
              <IconButton
                size='small'
                aria-label='Clear search'
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClear}
                sx={{ color: 'var(--mui-palette-text-primary)' }}
              >
                <i className='material-symbols-close-rounded' />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
});
